'use client';
import React, { useState, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../authContext/context';
import { UserRole } from '../constants/auth.constants';
import './Sidebar.css';

export default function SideBar() {
  const [openMenu, setOpenMenu] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { permissions, isSuperAdmin, role } = useContext(AuthContext);

  // Helper function to check permissions
  const hasPermission = (permission) => {
    // Superadmin ko sab dikhe
    if (isSuperAdmin) return true;
    
    if (!permissions || !permission) return false; // Hide if no permissions set
    
    const parts = permission.split('.');
    let current = permissions;
    
    for (const part of parts) {
      if (!current || typeof current !== 'object') return false;
      current = current[part];
    }
    
    return current === true;
  };
  
  // Check if user has any permission other than customers
  const hasOnlyCustomerPermissions = !isSuperAdmin && permissions && 
    (permissions.customers?.create || permissions.customers?.view_details || 
     permissions.customers?.edit || permissions.customers?.delete) &&
    !permissions.leads?.view && 
    !permissions.leads?.create &&
    !permissions.leads?.edit &&
    !permissions.leads?.delete &&
    !permissions.leads?.revise_quotes &&
    !permissions.leads?.change_group_code &&
    !permissions.notes?.view &&
    !permissions.notes?.create &&
    !permissions.notes?.edit &&
    Object.keys(permissions.settings || {}).every(key => !permissions.settings[key]) &&
    Object.keys(permissions.api || {}).every(key => !permissions.api[key]);

  const toggleMenu = (menuName) => {
    if (isCollapsed) return;
    setOpenMenu(openMenu === menuName ? null : menuName);
  };

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
    setOpenMenu(null);
  };

  return (
    <>
      <div className={`modern-sidebar ${isCollapsed ? 'collapsed' : ''}`}>
        {/* Header Section */}
        <div className="sidebar-header">
          <div className="logo-section">
            <div className="logo-icon">
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                <rect width="32" height="32" rx="8" fill="url(#gradient)" />
                <path d="M8 12h16M8 16h16M8 20h16" stroke="white" strokeWidth="2" strokeLinecap="round" />
                <defs>
                  <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#667eea" />
                    <stop offset="100%" stopColor="#764ba2" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
            {!isCollapsed && (
              <div className="logo-text">
                <h2>Dashboard</h2>
                <span>Admin Panel</span>
              </div>
            )}
          </div>
          
          <button className="sidebar-toggle" onClick={toggleSidebar}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              {isCollapsed ? (
                <path d="M3 9h12M3 5h12M3 13h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              ) : (
                <path d="M14 4L4 14M4 4l10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              )}
            </svg>
          </button>
        </div>

        {/* Navigation Section */}
        <nav className="sidebar-nav">
          {/* Main Navigation */}
          <div className="nav-section">
            {!isCollapsed && <div className="section-title">Main</div>}

            {/* Role-Based Dashboard */}
           

            {(hasPermission('customers.view_details') || 
              hasPermission('customers.create') || 
              hasPermission('customers.edit') || 
              hasPermission('customers.delete') ||
              hasPermission('leads.create') || 
              hasPermission('leads.view')) && (
              <Link to="/customers" className="nav-item">
                <div className="nav-icon">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M13 7a4 4 0 11-8 0 4 4 0 018 0zM9 14a6 6 0 016 6H3a6 6 0 016-6z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                {!isCollapsed && <span className="nav-text">Customers</span>}
                {isCollapsed && <div className="tooltip">Customers</div>}
              </Link>
            )}

            {(hasPermission('leads.create') || 
              hasPermission('leads.view') || 
              hasPermission('leads.edit') || 
              hasPermission('leads.delete') || 
              hasPermission('leads.revise_quotes') || 
              hasPermission('leads.change_group_code')) && (
              <Link to="/leads" className="nav-item">
                <div className="nav-icon">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                {!isCollapsed && <span className="nav-text">Leads</span>}
                {isCollapsed && <div className="tooltip">Leads</div>}
              </Link>
            )}

            {!hasOnlyCustomerPermissions && (
              <Link to="/tasks" className="nav-item">
                <div className="nav-icon">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M9 5H7a2 2 0 00-2 2v6a2 2 0 002 2h6a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                {!isCollapsed && <span className="nav-text">My Tasks</span>}
                {isCollapsed && <div className="tooltip">My Tasks</div>}
              </Link>
            )}
          </div>

          {/* Reporting Section */}
          {!hasOnlyCustomerPermissions && (
            <div className="nav-section">
              {!isCollapsed && <div className="section-title">Reporting</div>}
              
               <Link to="/dashboard" className="nav-item">
              <div className="nav-icon">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              {!isCollapsed && <span className="nav-text">Dashboard</span>}
              {isCollapsed && <div className="tooltip">Dashboard</div>}
            </Link>

            <div className={`nav-item dropdown-trigger ${openMenu === 'reports' ? 'active' : ''}`} onClick={() => toggleMenu('reports')}>
              <div className="nav-icon">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M7 12l3-3 3 3m-6 2a9 9 0 118 0 9 9 0 01-8 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              {!isCollapsed && <span className="nav-text">Reports</span>}
              {!isCollapsed && (
                <div className={`dropdown-arrow ${openMenu === 'reports' ? 'open' : ''}`}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              )}
              {isCollapsed && <div className="tooltip">Reports</div>}
            </div>

            {openMenu === 'reports' && !isCollapsed && (
              <div className="dropdown-menu">
                <Link to="/reports" className="dropdown-item">
                  <span>Lead Overview</span>
                </Link>
              </div>
            )}
            </div>
          )}

          {/* Settings Section */}
          {!hasOnlyCustomerPermissions && (
            <div className="nav-section">
              {!isCollapsed && <div className="section-title">Settings</div>}
            
            {/* Users menu - only show if user has any user management permissions */}
            {(hasPermission('settings.manage_users') ||
              hasPermission('settings.manage_user_groups') ||
              hasPermission('settings.manage_user_group_permissions') ||
              hasPermission('settings.manage_partners') ||
              hasPermission('settings.manage_solicitors')) && (
            <div className={`nav-item dropdown-trigger ${openMenu === 'user' ? 'active' : ''}`} onClick={() => toggleMenu('user')}>
              <div className="nav-icon">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0v-1a6 6 0 00-9-5.197m0 0A4 4 0 013 11a4 4 0 016.023-3.477c.3.177.596.394.877.65z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              {!isCollapsed && <span className="nav-text">Users</span>}
              {!isCollapsed && (
                <div className={`dropdown-arrow ${openMenu === 'user' ? 'open' : ''}`}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              )}
              {isCollapsed && <div className="tooltip">Users</div>}
            </div>
            )}

            {openMenu === 'user' && !isCollapsed && (
              <div className="dropdown-menu">
                {hasPermission('settings.manage_users') && (
                  <Link to="/users" className="dropdown-item">Users</Link>
                )}
                {hasPermission('settings.manage_user_groups') && (
                  <Link to="/user-groups" className="dropdown-item">User Groups</Link>
                )}
                {hasPermission('settings.manage_partners') && role !== UserRole.PARTNER_USER && (
                  <Link to="/partners" className="dropdown-item">Partners</Link>
                )}
                {hasPermission('settings.manage_partners') && role !== UserRole.PARTNER_USER && (
                  <Link to="/partner-users" className="dropdown-item">Partner Users</Link>
                )}
                {hasPermission('settings.manage_solicitors') && (
                  <Link to="/solicitors" className="dropdown-item">Solicitors</Link>
                )}
              </div>
            )}

            {/* Communication menu - only show if user has any communication permissions */}
            {(hasPermission('settings.manage_email_templates') ||
              hasPermission('settings.manage_sms_templates')) && (
            <div className={`nav-item dropdown-trigger ${openMenu === 'communication' ? 'active' : ''}`} onClick={() => toggleMenu('communication')}>
              <div className="nav-icon">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              {!isCollapsed && <span className="nav-text">Communication</span>}
              {!isCollapsed && (
                <div className={`dropdown-arrow ${openMenu === 'communication' ? 'open' : ''}`}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              )}
              {isCollapsed && <div className="tooltip">Communication</div>}
            </div>
            )}

            {openMenu === 'communication' && !isCollapsed && (
              <div className="dropdown-menu">
                {hasPermission('settings.manage_email_templates') && (
                  <Link to="/emailtemplates" className="dropdown-item">Email Templates</Link>
                )}
                {hasPermission('settings.manage_email_templates') && (
                  <Link to="/communication/email-theming" className="dropdown-item">Email Theming</Link>
                )}
                {hasPermission('settings.manage_sms_templates') && (
                  <Link to="/smstemplates" className="dropdown-item">SMS Templates</Link>
                )}
              </div>
            )}

            {/* System Setup - Only show if user has any system setup permissions */}
            {(hasPermission('settings.configure_automation') ||
              hasPermission('settings.configure_task_definition') ||
              hasPermission('api.create_api_keys') ||
              hasPermission('api.disable_api_keys') ||
              hasPermission('settings.configure_tags') ||
              hasPermission('settings.manage_group_codes') ||
              hasPermission('settings.manage_lender_panels') ||
              hasPermission('settings.manage_custom_lead_fees') ||
              hasPermission('settings.manage_lead_sub_statuses')) && (
            <div className={`nav-item dropdown-trigger ${openMenu === 'System' ? 'active' : ''}`} onClick={() => toggleMenu('System')}>
              <div className="nav-icon">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              {!isCollapsed && <span className="nav-text">System Setup</span>}
              {!isCollapsed && (
                <div className={`dropdown-arrow ${openMenu === 'System' ? 'open' : ''}`}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              )}
              {isCollapsed && <div className="tooltip">System Setup</div>}
            </div>
            )}

            {openMenu === 'System' && !isCollapsed && (
              <div className="dropdown-menu">
                {!hasOnlyCustomerPermissions && (
                  <Link to="/products" className="dropdown-item">Products</Link>
                )}
                {hasPermission('settings.configure_automation') && (
                  <Link to="/automation" className="dropdown-item">Automation</Link>
                )}
                {hasPermission('settings.configure_task_definition') && (
                  <Link to="/system-setup/taskdefinitions" className="dropdown-item">Task Definitions</Link>
                )}
                {(hasPermission('api.create_api_keys') || hasPermission('api.disable_api_keys')) && (
                  <Link to="/apiKeys" className="dropdown-item">API Keys</Link>
                )}
                {hasPermission('settings.configure_tags') && (
                  <Link to="/tags" className="dropdown-item">Tags</Link>
                )}
                {hasPermission('settings.manage_group_codes') && (
                  <Link to="/groupCodes" className="dropdown-item">Group Codes</Link>
                )}
                {hasPermission('settings.manage_lender_panels') && (
                  <Link to="/lenderPanels" className="dropdown-item">Lender Panels</Link>
                )}
                {hasPermission('settings.manage_custom_lead_fees') && (
                  <Link to="/custom/lead-fees" className="dropdown-item">Custom Lead Fees</Link>
                )}
                {hasPermission('settings.manage_lead_sub_statuses') && (
                  <Link to="/system-setup/LeadSubStatuses" className="dropdown-item">Lead Sub Status</Link>
                )}
              </div>
            )}

            {/* Branches menu - only show if user has branches permission */}
            {hasPermission('settings.manage_branches') && (
            <div className={`nav-item dropdown-trigger ${openMenu === 'Branch' ? 'active' : ''}`} onClick={() => toggleMenu('Branch')}>
              <div className="nav-icon">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              {!isCollapsed && <span className="nav-text">Branches</span>}
              {!isCollapsed && (
                <div className={`dropdown-arrow ${openMenu === 'Branch' ? 'open' : ''}`}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              )}
              {isCollapsed && <div className="tooltip">Branches</div>}
            </div>
            )}

            {openMenu === 'Branch' && !isCollapsed && (
              <div className="dropdown-menu">
                {hasPermission('settings.manage_branches') && (
                  <Link to="/branches" className="dropdown-item">Branches</Link>
                )}
              </div>
            )}

            {/* Partner Rate Cards menu - only show if user has partners permission */}
            {hasPermission('settings.manage_partners') && (
            <div className={`nav-item dropdown-trigger ${openMenu === 'PartnerRateCards' ? 'active' : ''}`} onClick={() => toggleMenu('PartnerRateCards')}>
              <div className="nav-icon">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M9 12h3.75M9 15h3.75M9 18c-4.51 0-8.16-3.69-8.16-8.25S4.49 1.5 9 1.5h2.3c4.51 0 8.16 3.69 8.16 8.25S15.81 18 11.3 18H9z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              {!isCollapsed && <span className="nav-text">Partner Rate Cards</span>}
              {!isCollapsed && (
                <div className={`dropdown-arrow ${openMenu === 'PartnerRateCards' ? 'open' : ''}`}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              )}
              {isCollapsed && <div className="tooltip">Partner Rate Cards</div>}
            </div>
            )}

            {openMenu === 'PartnerRateCards' && !isCollapsed && (
              <div className="dropdown-menu">
                {hasPermission('settings.manage_partners') && (
                  <Link to="/partner-rate-cards" className="dropdown-item">Partner Rate Cards</Link>
                )}
              </div>
            )}
            </div>
          )}
        </nav>

        {/* Footer */}
        {/* {!isCollapsed && (
          <div className="sidebar-footer">
            <div className="user-profile">
              <div className="user-avatar">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
                </svg>
              </div>
              <div className="user-info">
                <div className="user-name">John Doe</div>
                <div className="user-role">Administrator</div>
              </div>
            </div>
          </div>
        )} */}
      </div>
      
      {/* Mobile Overlay
      {!isCollapsed && (
        <div className="sidebar-overlay" onClick={toggleSidebar}></div>
      )} */}
    </>
  );
}